import {
    takeEvery,
    all,
    call,
    put,
    select,
    fork
} from 'redux-saga/effects';
import moment from 'moment';
import fp from 'lodash/fp';
import { push } from 'connected-react-router';
import urlJoin from 'url-join';

import actionFactory from '../webcommon/header/filterheader/actions';
import * as filterHeaderSelectors from '../webcommon/header/filterheader/selectors';

import * as pricingParametersSelectors from './pricingparameters/selectors';
import * as pricingParametersActions from './pricingparameters/actions';
import * as tradesPricedTodayActions from './impactsummary/tradepricedtoday/actions';
import * as impactDetailedActions from './impactdetailed/actions';
import * as imImpactSummaryActions from './impactsummary/imimpactsummary/actions';
import * as idSummaryActions from './impactAnalysisIdSummary/actions';
import * as idSummarySelectors from './impactAnalysisIdSummary/selectors';
import * as summaryActions from './summary/actions';
import { csaRequest } from '../api/imImpactSummaryApi';
import * as pricingParamsApi from '../api/pricingParamsApi';
import * as impactAnalysisApi from '../api/impactAnalysis';
import { tradePricedTodayRequest } from '../api/tradePricedTodayApi';
import * as appSelectors from '../app/selectors';
import * as summarySelectors from './summary/selectors';
import * as appConstants from '../common/constants';
import { getVirtualDirectory } from '../common/helpermethods/HelperMethods';

const filterHeaderActions = actionFactory('ImpactAnalysisIdSummary');

export const PAGE_ROUTE_IMPACT_ANALYSIS = urlJoin(getVirtualDirectory(), '/impactanalysis');

export function* loadTradesPricedToday(tradeInfoList) {
    yield put(tradesPricedTodayActions.setTradesPricedToday(tradeInfoList));
}

export function* loadImpactSummary(csaSummaries) {
    yield put(imImpactSummaryActions.setImpactSummaryRows(csaSummaries));
    yield put(imImpactSummaryActions.setLoading(false));
}

export function* loadImpactDetailed(drildowns) {
    yield put(impactDetailedActions.setImpactDetailedRows(drildowns));
    yield put(impactDetailedActions.setLoading(false));
}

export function* loadSummaryTradesPricedToday(tradeInfoList) {
    yield put(idSummaryActions.setSummaryTradesPricedToday(tradeInfoList));
}

export function* loadSummaryImpactSummary(csaSummaries) {
    yield put(idSummaryActions.setSummaryImpactSummaryRows(csaSummaries));
    yield put(imImpactSummaryActions.setLoading(false));
}

// This saga is common to the 3 ways new data is loaded on the screen
// - Trades being booked
// - Compare CSA dropdown changing
// - Pricing Parameters grid submission
export function* loadNewPageData(api, key, action) {
    try {
        const runDate = yield select(pricingParametersSelectors.getDateFrom);
        const csa = yield select(pricingParametersSelectors.getActiveCsa);
        yield put(pricingParametersActions.setLoading(true));
        yield put(imImpactSummaryActions.setLoading(true));
        yield put(impactDetailedActions.setLoading(true));
        let request = {
            csa,
            runDate: moment(runDate).format(appConstants.dateFormat)
        };
        request = fp.set(key, action.data, request);
        const csaSummaryInfo = yield call(api, request);
        const csas = yield select(appSelectors.getCsas);

        csaSummaryInfo.csaSummaries = fp.map((csaSummaryRow) => {
            if (csaSummaryRow.titleRow) {
                const csaId = csaSummaryRow.title;
                const csaDetail = fp.find(csa => csa.value === csaId, csas);
                return fp.set('counterparty', csaDetail.counterparty, csaSummaryRow);
            }

            return csaSummaryRow;
        }, csaSummaryInfo.csaSummaries);

        csaSummaryInfo.csaDrilldowns = fp.map((csaDrilldownRow) => {
            if (csaDrilldownRow.titleRow) {
                const csaId = csaDrilldownRow.imModel;
                const csaDetail = fp.find(csa => csa.value === csaId, csas);
                return fp.set('counterparty', csaDetail.counterparty, csaDrilldownRow);
            }

            return csaDrilldownRow;
        }, csaSummaryInfo.csaDrilldowns);
        yield put(pricingParametersActions.setLoading(false));
        yield put(pricingParametersActions.setPricingParametersRows([]));
        yield put(pricingParametersActions.addPricingParametersRow());
        yield fork(loadTradesPricedToday, csaSummaryInfo.tradeInfoList);
        yield fork(loadImpactSummary, csaSummaryInfo.csaSummaries);
        yield fork(loadImpactDetailed, csaSummaryInfo.csaDrilldowns);
        yield put(summaryActions.setCurrentCsaStatus(fp.get('csaSummaries[0].monitorStatus')(csaSummaryInfo)));
        yield put(summaryActions.setTransactionId(fp.get('impactAnalysisId')(csaSummaryInfo)));
    } catch (error) {
        yield put(imImpactSummaryActions.compareCsasRequestFailed(error.message));
        yield put(impactDetailedActions.setLoading(false));
        yield put(imImpactSummaryActions.setLoading(false));
        yield put(pricingParametersActions.setLoading(false));
    }
}

export function* submitPricingParams(api, action) {
    try {
        const runDate = yield select(pricingParametersSelectors.getDateFrom);
        const csa = yield select(pricingParametersSelectors.getActiveCsa);
        yield put(pricingParametersActions.setLoading(true));
        yield put(imImpactSummaryActions.setLoading(true));
        yield put(impactDetailedActions.setLoading(true));
        const request = {
            csa,
            runDate: moment(runDate).format(appConstants.dateFormat),
            pricingParameters: action.data
        };

        const submitPricingParamsErrorMessage = yield call(api.validatePricingParams, request);
        if (submitPricingParamsErrorMessage.length > 0) {
            yield put(pricingParametersActions.submitPricingParametersInvalid(`The pricing parameters data is invalid. ${submitPricingParamsErrorMessage}`));
        } else {
            const csaSummaryInfo = yield call(api.submitPricingParamsRows, request);
            const csas = yield select(appSelectors.getCsas);

            csaSummaryInfo.csaSummaries = fp.map((csaSummaryRow) => {
                if (csaSummaryRow.titleRow) {
                    const csaId = csaSummaryRow.title;
                    const csaDetail = fp.find(csa => csa.value === csaId, csas);
                    return fp.set('counterparty', csaDetail.counterparty, csaSummaryRow);
                }

                return csaSummaryRow;
            }, csaSummaryInfo.csaSummaries);

            csaSummaryInfo.csaDrilldowns = fp.map((csaDrilldownRow) => {
                if (csaDrilldownRow.titleRow) {
                    const csaId = csaDrilldownRow.imModel;
                    const csaDetail = fp.find(csa => csa.value === csaId, csas);
                    return fp.set('counterparty', csaDetail.counterparty, csaDrilldownRow);
                }

                return csaDrilldownRow;
            }, csaSummaryInfo.csaDrilldowns);
            yield put(pricingParametersActions.setPricingParametersRows([]));
            yield put(pricingParametersActions.addPricingParametersRow());
            yield fork(loadTradesPricedToday, csaSummaryInfo.tradeInfoList);
            yield fork(loadImpactSummary, csaSummaryInfo.csaSummaries);
            yield fork(loadImpactDetailed, csaSummaryInfo.csaDrilldowns);
            yield put(summaryActions.setCurrentCsaStatus(fp.get('csaSummaries[0].monitorStatus')(csaSummaryInfo)));
            yield put(summaryActions.setTransactionId(fp.get('impactAnalysisId')(csaSummaryInfo)));
        }
    } catch (error) {
        yield put(pricingParametersActions.submitPricingParametersFailed(error.message));
    } finally {
        yield put(impactDetailedActions.setLoading(false));
        yield put(imImpactSummaryActions.setLoading(false));
        yield put(pricingParametersActions.setLoading(false));
    }
}

export function* initialiseFilterHeader() {
    const initialised = yield select(idSummarySelectors.getInitialised);
    if (!initialised) {
        try {
            yield put(summaryActions.setLoading(true));
            const filterDefinitions = yield select(summarySelectors.getFilterDefinitions);
            yield put(filterHeaderActions.setFilterDefinitions(filterDefinitions));
            const defaultFilters = yield select(summarySelectors.getDefaultFilters);
            yield put(filterHeaderActions.setDefaultFilters(defaultFilters));
        } finally {
            yield put(idSummaryActions.setInitialised());
        }
    }
}

export function* setFilterHeaders(csa, date) {
    const csaList = yield select(appSelectors.getCsas);
    const counterpartyObj = fp.find(csaObj => csaObj.value === csa)(csaList);
    const counterparty = fp.get('counterparty')(counterpartyObj);
    const eodDate = moment(date).format(appConstants.dateFormat);
    yield put(filterHeaderActions.setAvailableListValues('counterparty', [{ title: counterparty, value: counterparty }]));
    yield put(filterHeaderActions.setAvailableListValues('csa', [{ title: csa, value: csa, counterparty }]));

    const activeFilters = yield select(filterHeaderSelectors.getFilters, 'ImpactAnalysisIdSummary');
    const eodFilterIndex = fp.findIndex(filter => filter.definition.id === 'runDate')(activeFilters);
    const csaFilterIndex = fp.findIndex(filter => filter.definition.id === 'csa')(activeFilters);
    const counterpartyFilterIndex = fp.findIndex(filter => filter.definition.id === 'counterparty')(activeFilters);


    const newFilters = fp.flow(
        fp.set(`[${csaFilterIndex}].value`, [csa]),
        fp.set(`[${counterpartyFilterIndex}].value`, [counterparty]),
        fp.set(`[${eodFilterIndex}].value.from`, eodDate)
    )(activeFilters);

    yield put(filterHeaderActions.setFilters(newFilters));
}

export function* searchId(api, action) {
    try {
        yield put(imImpactSummaryActions.setLoading(true));
        yield put(pricingParametersActions.setLoading(true));
        const data = yield call(api.searchImpactId, action.id);
        const impactData = data.csaImpact;
        const pricingParams = data.trades;
        if (!impactData.csa) {
            yield put(idSummaryActions.incorrectImpactId('The Impact Analysis ID is incorrect'));
            yield put(push(`${PAGE_ROUTE_IMPACT_ANALYSIS}`));
            yield put(imImpactSummaryActions.setLoading(false));
            yield put(pricingParametersActions.setLoading(false));
        } else {
            const initialised = yield select(idSummarySelectors.getInitialised);
            if (!initialised) { yield fork(initialiseFilterHeader); }
            yield fork(setFilterHeaders, impactData.csa, impactData.runDate);
            yield put(summaryActions.setCurrentCsaStatus(fp.get('csaSummaries[0].monitorStatus')(impactData)));
            yield put(idSummaryActions.setImpactAnalysisId(impactData.impactAnalysisId));
            yield put(idSummaryActions.setPricingParams(pricingParams));
            yield fork(loadSummaryTradesPricedToday, impactData.tradeInfoList);
            yield fork(loadSummaryImpactSummary, impactData.csaSummaries);
            yield put(imImpactSummaryActions.setImpactSummaryLoaded(true));
            yield put(pricingParametersActions.setPricingParametersInfoLoaded(true));
            yield put(pricingParametersActions.setLoading(false));
        }
    } catch (error) {
        yield put(idSummaryActions.SearchIdError(error.message));
    }
}

export default function* rootSaga() {
    yield all([
        takeEvery(pricingParametersActions.SUBMIT_PRICING_PARAMETERS,
            submitPricingParams, pricingParamsApi),
        takeEvery(imImpactSummaryActions.COMPARE_CSAS_REQUEST,
            loadNewPageData, csaRequest, 'csaList'),
        takeEvery(tradesPricedTodayActions.SUBMIT_BOOKING_TRADES,
            loadNewPageData, tradePricedTodayRequest, 'tradeList'),
        takeEvery(idSummaryActions.SEARCH_ID_REQUEST,
            searchId, impactAnalysisApi),
        takeEvery(idSummaryActions.INITIALISE,
            initialiseFilterHeader)
    ]);
}
